import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import meetingService from '../../services/meetingService';
import HMSMeetingRoom from './HMSMeetingRoom';

interface MeetingRoomProps {
  meetingId: string;
  onLeave: () => void;
}

interface MeetingRoomState {
  token: string | null;
  roomCode: string | null;
  userRole: string | null;
  loading: boolean;
  error: string | null;
}

const MeetingRoom: React.FC<MeetingRoomProps> = ({ meetingId, onLeave }) => {
  const { user } = useAuth();
  const [state, setState] = useState<MeetingRoomState>({
    token: null,
    roomCode: null,
    userRole: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    joinMeeting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const joinMeeting = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const tokenResponse = await meetingService.getMeetingToken(meetingId);
      
      setState(prev => ({
        ...prev,
        token: tokenResponse.token,
        roomCode: tokenResponse.room_code,
        userRole: tokenResponse.role,
        loading: false,
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Failed to join meeting',
      }));
    }
  };

  const leaveMeeting = async () => {
    try {
      await meetingService.leaveMeeting(meetingId);
      onLeave();
    } catch (error) {
      console.error('Error leaving meeting:', error);
      onLeave(); // Still leave the UI even if API call fails
    }
  };

  if (state.loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ color: '#666666' }}>
          Joining meeting...
        </Typography>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {state.error}
        </Alert>
        <Button variant="contained" onClick={joinMeeting}>
          Try Again
        </Button>
        <Button variant="outlined" onClick={onLeave}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!state.token || !state.roomCode) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: '#666666' }}>
          No meeting token available
        </Typography>
        <Button variant="contained" onClick={joinMeeting}>
          Join Meeting
        </Button>
      </Box>
    );
  }

  return (
    <HMSMeetingRoom
      token={state.token}
      roomCode={state.roomCode}
      userName={`${user?.first_name} ${user?.last_name}`}
      userRole={state.userRole || 'participant'}
      onLeave={leaveMeeting}
    />
  );
};

export default MeetingRoom;
